import DialogInfoGeneral from "./DialogInfoGeneral";
import { FormEvent, useEffect, useRef, useState } from "react";
import { dateToLocalHour, getProfileDefaultImage } from "src/helpers/mixins";
import { MessageTransfer } from "src/types";
import APIService from "src/services/Api";
import { UseAuth } from "src/auth/AuthContext";
import { ImSpinner9 } from "react-icons/im";
import { io, Socket } from "socket.io-client"; // Importa el tipo Socket
import { IoMdSend } from "react-icons/io";
import FormatLastConnection from "./FormatLastConnection";
import { BiMessageRoundedDots } from "react-icons/bi";

type ParamsOpenChatTransfer = {
  destinyPhotoUrl: string;
  destinyFullname: string;
  depositId: string | number;
  destinyLastConnection: string 
  status: string
};
const OpenChatTransfer: React.FC<ParamsOpenChatTransfer> = ({
  destinyPhotoUrl,
  destinyFullname,
  depositId,
  destinyLastConnection,
  status
}) => {
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Estado para la carga
  const [messages, setMessages] = useState<MessageTransfer[]>([]); // Estado para los mensajes
  const { user } = UseAuth();
  const [socket, setSocket] = useState<Socket | null>(null); // Estado para el socket
  const [chatId, setChatId] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState<string>("");
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const newMessageQuantity = useRef(0);

  const [loadingSending, setLoadingSending] = useState<boolean>(false);
  const onChangeInput = (e: FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setMessageInput(value);
  };

  const fetchChatId = async () => {
    try {
      const r = await APIService.findChatId(depositId);
      if (r && r.data && r.data.data && r.data.data.chatId) {
        setChatId(r.data.data.chatId);
      } else {
        console.error("Error al obtener chatId:", r);
      }
    } catch (error) {
      console.error("Error en fetchChatId:", error);
    }
  };
  const saveMessage = async (e?: any) => {
    if(e){
      e.preventDefault();
    }
    setLoadingSending(true);
    try {
      const newMessage = {
        // Crea el nuevo mensaje *antes* de enviarlo
        id: "temp-" + Date.now(), // ID temporal
        chat: { id: chatId },
        content: messageInput,
        createdAt: new Date(), // Fecha actual
        user: {
          id: user?.id,
          fullname: user?.fullname,
          photoUrl: user?.photoUrl,
        },
      };
      setMessages((prevMessages) => [...prevMessages, newMessage as any]); // Actualiza *inmediatamente* el estado con el mensaje temporal
      scrollToBottom();
      setMessageInput("");      
      if (chatId) {
        await APIService.chatTransferSaveMessage({
          depositId,
          chatId,
          userId: user?.id || "",
          content: messageInput,
        });
      }
      fetchMessages();
      if (socket) {
        socket.emit("chatMessage", { ...newMessage, chatId }); // Incluye chatId
        console.log('emitiendo chat message')
      } else {
        console.log("no fue posible emitir socket");
      }
    } catch (e) {
      console.log("error guardando mensaje", e);
    } finally {
      setLoadingSending(false);
    }
    setLoadingSending(false);
  };
  const fetchMessages = async () => {
    setIsLoading(true);
    try {
      const response = await APIService.chatTransferListMessage(depositId);
      const messages = response?.data?.data?.messages;
      setMessages(messages);
      scrollToBottom();
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      (messagesEndRef.current as HTMLDivElement).scrollIntoView( );
    }
  };
  useEffect(() => {
    scrollToBottom();
  }, []);  

  useEffect(() => {
    if(show){
      scrollToBottom();
    }
  }, [show])
  

  useEffect(() => {
    const observer = new MutationObserver(scrollToBottom);
    if (chatContainerRef.current) {
      observer.observe(chatContainerRef.current, { childList: true, subtree: true });
    }

    return () => {
      if (chatContainerRef.current) {
        observer.disconnect();
      }
    };
  }, []);


  useEffect(() => {
    fetchChatId();
    const newSocket = io(process.env.REACT_APP_API || "");
    setSocket(newSocket);

    newSocket.on("connect", () => {
      console.log("Conectado al servidor de sockets");
    });

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [depositId]);

  useEffect(() => {
    if (socket && (chatId && chatId?.length > 0)) {
      /* console.log('JOININ IN CHAT ',chatId) */
      socket.emit("joinChat", chatId);
      fetchMessages();
      socket.on("nuevoMensaje", (mensaje) => {
        setMessages((prevMessages) => {
          /* console.log('NUEVO MENSAJE RECIBIDO') */
          if(mensaje.user.id !== user?.id && !show){
            newMessageQuantity.current =    newMessageQuantity.current +1
          }
          const messageExists = prevMessages.find((m) => m.id === mensaje.id);
          if (!messageExists) {
            return [...prevMessages, mensaje];
          }
          return prevMessages;
        });
        scrollToBottom()
      });
    }
  }, [chatId, socket]);

  useEffect(() => {
    newMessageQuantity.current = 0
  }, [show])
  
  return (
    <div>
      <div
        onClick={() => setShow(true)}
        className="bottom-20 right-10 md:right-0 md:bottom-0 z-10 fixed md:relative flex items-center gap-2 button-chat-web border border-b-2 border-amber-400 px-2 py-2 w-fit rounded-md cursor-pointer"
      >
        <div className="flex items-center">
          <div className="text-xs hidden uppercase font-bold opensans tracking-wider md:flex">Chatea conmigo</div>
          {newMessageQuantity.current > 0 && 
            <span className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center absolute -top-3 -right-3">{newMessageQuantity.current}</span>
          }
        </div>
        < BiMessageRoundedDots size={27} />
      </div>

      <div>
        <DialogInfoGeneral
          show={show}
          closeDialog={() => setShow(false)}
          disabledFooter={true}
        >
          <div>
            <div className="flex items-center gap-2 button-chat-web border-b border-amber-300 px-4 py-2">
              <div
                className="w-10 h-10 bg-cover bg-center rounded-full"
                style={{
                  backgroundImage: `url(${
                    (destinyPhotoUrl?.length || 0) > 0
                      ? process.env.REACT_APP_PROFILEPICTURE +
                        (destinyPhotoUrl || "")
                      : getProfileDefaultImage()
                  })`,
                }}
              ></div>
              <div className="text-sm sm:text-sm md:text-base opensans">
                {destinyFullname}
                <div className="text-xs">
                  <FormatLastConnection lastConnection={destinyLastConnection} />
                </div>
              </div>
            </div>
            <div className="bg-white p w-full px-4">
              <div
                ref={chatContainerRef}
                className="bg-white body-chat-popup w-full px-4 pb-10 overflow-y-auto h-96 opensans text-sm"
              >
                <>
                <div className="font-bold py-2"> Para su seguridad, realice todas las transferencias únicamente dentro de la plataforma.</div>
                {!isLoading && messages.length === 0 &&
                  <div>
                    Inicia un conversación.
                  </div>
                }
                {/* Altura fija y scroll */}
                {messages?.map((message) => (
                  <div
                    key={message.id.toString()}
                    className={`flex items-start gap-2 mt-2 ${
                      message.user.id === user?.id // ID del usuario logueado
                        ? "justify-end" // Alinea a la derecha si es el usuario logueado
                        : "" // Alinea a la izquierda si es el otro usuario
                    }`}
                  >
                    <div
                      className="w-5 h-5 bg-cover bg-center rounded-full relative top-1"
                      style={{
                        backgroundImage: `url(${
                          (message.user.photoUrl?.length || 0) > 0
                            ? process.env.REACT_APP_PROFILEPICTURE +
                              (message.user.photoUrl || "")
                            : getProfileDefaultImage()
                        })`,
                      }}
                    ></div>
                    <div
                      className={`rounded-lg px-2 py-0.5 font-semibold ${
                        message.user.id === user?.id
                          ? "bg-sky-100 text-sky-800" // Estilo para mensajes del usuario logueado
                          : "bg-gray-100 text-gray-800" // Estilo para mensajes del otro usuario
                      }`}
                    >
                      {message.content}
                      <div className="text-[.7rem] text-gray-400">
                        {dateToLocalHour(message.createdAt)}{" "}
                      </div>
                    </div>
                  </div>
                ))}
                </>
                <div ref={messagesEndRef} />
              </div>   
            </div>
              {status !=='waitpay' && status !== 'checking'  && 
                <div className="button-chat-web py-2 px-2 text-xs font-semibold">
                  Esta transferencia se ha cerrado. El chat solo se encuentra habilitado para transferencias en curso.
                </div>
              }
            {(user?.role ==='cashier' || user?.role ==='user') && 
              (status ==='waitpay' || status === 'checking') &&
              <form onSubmit={saveMessage}>
                <div className="button-chat-web border-t border-stone-300 flex items-center gap-1 w-full px-4 text-lg py-2">
                  <input
                    type="text"
                    onChange={onChangeInput}
                    value={messageInput}
                    className="bg-white-100 w-11/12 px-4 py-2 text-base rounded-md border border-stone-300"
                    placeholder="Escribir mensaje.."
                  />
                  {!loadingSending ? (
                    <div className="bg-yellow-400 px-2 py-2 rounded-full">
                      <IoMdSend
                        onClick={() => saveMessage()}
                        size={20}
                        className="cursor-pointer  text-white"
                      />
                    </div>
                  ) : (
                    <ImSpinner9
                      className="animate-spin"
                      onClick={() => saveMessage()}
                      size={30}
                    />
                  )}
                </div>
              </form>
            }
          </div>
        </DialogInfoGeneral>
      </div>
    </div>
  );
};

export default OpenChatTransfer;
